/**
 * Список сообщений чата Activity.
 * Отвечает за loading/empty состояния и правило показа автора
 * в последовательности комментариев.
 */
import { memo } from 'react';
import { Flex, Text } from '@mantine/core';
import type { ActivityChatItem } from '@ayocore/activity/common/types/activity-message';
import { ActivityChatMessageItem } from './ActivityChatMessageItem';

type ActivityChatMessageListProps = {
  chatItems: ActivityChatItem[];
  chatLoading: boolean;
  onItemAction?: (item: ActivityChatItem, actionId: string) => void;
  onInsertUserMention?: (userId: number, label: string) => void;
};

function ActivityChatMessageListComponent({
  chatItems,
  chatLoading,
  onItemAction,
  onInsertUserMention,
}: ActivityChatMessageListProps) {
  const shouldShowAuthor = (item: ActivityChatItem, previous: ActivityChatItem | null): boolean => {
    if (item.kind !== 'comment') return false;
    if (item.is_own) return false;
    if (!previous || previous.kind !== 'comment' || previous.is_own) return true;

    const hasAuthorIds = item.author_id != null && previous.author_id != null;
    if (hasAuthorIds) {
      return item.author_id !== previous.author_id;
    }

    return item.author_name !== previous.author_name;
  };

  return (
    <>
      {chatLoading ? (
        <Flex align='center' justify='center' gap='xs' mih={180} flex={1} direction='column'>
          <Text size='xs' c='white'>
            Загрузка сообщений...
          </Text>
        </Flex>
      ) : null}

      {!chatLoading && chatItems.length === 0 ? (
        <Flex align='center' justify='center' gap='xs' mih={180} flex={1} direction='column'>
          <Text size='xs' c='white'>
            Сообщений пока нет.
          </Text>
        </Flex>
      ) : null}

      {!chatLoading &&
        chatItems.map((item, index) => (
          <ActivityChatMessageItem
            key={item.id}
            item={item}
            showAuthor={shouldShowAuthor(item, index > 0 ? chatItems[index - 1] : null)}
            onAction={onItemAction}
            onInsertUserMention={onInsertUserMention}
          />
        ))}
    </>
  );
}

export const ActivityChatMessageList = memo(
  ActivityChatMessageListComponent,
  (prev, next) =>
    prev.chatItems === next.chatItems &&
    prev.chatLoading === next.chatLoading &&
    prev.onItemAction === next.onItemAction &&
    prev.onInsertUserMention === next.onInsertUserMention
);
